/*
  Schema-driven renderer for a single assessment field. Extracted from
  AssessmentEditor.jsx (Feb 2026) so other surfaces — starting with the
  EP Follow-Up capture panel inside SoapSessionDialog — can render the
  same field types without pulling in the full editor page (which would
  create a circular import with SoapSessionDialog).

  Field shape follows ``backend/assessments/module_defs.py``. Supported
  types: text, long-text, number, date, choice, yes-no-unsure, rating,
  table.
*/
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { bmiCategory, whtrCategory } from "@/lib/bodyMetrics";

export function FieldRenderer({ field, resp, onChange, readOnly }) {
  const value = resp?.value ?? "";
  const set = (v) => onChange(field.key, v);

  if (field.computed) {
    const cat = value === "" ? null : (field.computed === "bmi" ? bmiCategory : whtrCategory)(Number(value));
    return (
      <div className="flex items-center gap-2 h-9" data-testid={`f-${field.key}`}>
        <span className="font-mono text-sm text-zinc-900 px-3 py-1.5 rounded-md border border-dashed border-zinc-300 bg-zinc-50 min-w-[80px]"
              data-testid={`f-${field.key}-value`}>
          {value === "" ? "—" : Number(value).toFixed(field.computed === "bmi" ? 1 : 2)}
        </span>
        {cat ? (
          <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cat.tone}`}
                data-testid={`f-${field.key}-flag`}>{cat.label}</span>
        ) : (
          <span className="text-xs text-zinc-400">Enter {field.computed === "bmi" ? "weight and height" : "waist and height"}</span>
        )}
      </div>
    );
  }

  if (field.type === "text") {
    return <Input value={value} onChange={(e) => set(e.target.value)} disabled={readOnly}
      data-testid={`f-${field.key}`} />;
  }
  if (field.type === "long-text") {
    return <Textarea rows={3} value={value} onChange={(e) => set(e.target.value)} disabled={readOnly}
      data-testid={`f-${field.key}`} />;
  }
  if (field.type === "number") {
    return <Input type="number" value={value} onChange={(e) => set(e.target.value === "" ? "" : Number(e.target.value))}
      disabled={readOnly} data-testid={`f-${field.key}`} />;
  }
  if (field.type === "date") {
    return <Input type="date" value={value || ""} onChange={(e) => set(e.target.value)} disabled={readOnly}
      data-testid={`f-${field.key}`} />;
  }
  if (field.type === "choice") {
    return (
      <Select value={value || "_none"} onValueChange={(v) => set(v === "_none" ? "" : v)} disabled={readOnly}>
        <SelectTrigger data-testid={`f-${field.key}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">— none —</SelectItem>
          {(field.options || []).map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "yes-no-unsure") {
    const opts = [
      { v: "yes", label: "Yes" },
      { v: "no", label: "No" },
      { v: "unsure", label: "Unsure" },
    ];
    return (
      <div className="flex gap-1.5" data-testid={`f-${field.key}`}>
        {opts.map((o) => (
          <button
            key={o.v}
            type="button"
            disabled={readOnly}
            onClick={() => set(value === o.v ? "" : o.v)}
            className={`px-3 h-9 rounded-md border text-sm ${
              value === o.v ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-300 bg-white hover:bg-zinc-100"
            } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
            data-testid={`f-${field.key}-${o.v}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  if (field.type === "rating") {
    return (
      <div className="flex flex-wrap gap-1" data-testid={`f-${field.key}`}>
        {[0,1,2,3,4,5,6,7,8,9,10].map((n) => (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onClick={() => set(value === n ? "" : n)}
            className={`w-9 h-9 rounded-md border text-sm font-medium ${
              value === n ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-300 bg-white hover:bg-zinc-100"
            } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
            data-testid={`f-${field.key}-${n}`}
          >
            {n}
          </button>
        ))}
      </div>
    );
  }
  if (field.type === "table") {
    const rows = Array.isArray(value) ? value : [];
    const cols = field.columns || [];
    const updateRow = (idx, colKey, v) => {
      const next = [...rows];
      next[idx] = { ...(next[idx] || {}), [colKey]: v };
      set(next);
    };
    const addRow = () => set([...rows, {}]);
    const removeRow = (idx) => set(rows.filter((_, i) => i !== idx));
    return (
      <div className="space-y-2" data-testid={`f-${field.key}`}>
        <div className="overflow-x-auto rounded border border-zinc-200">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className="text-left px-2 py-1.5 font-medium text-zinc-700">{c.label}</th>
                ))}
                {!readOnly && <th className="px-2 py-1.5 w-8"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-t border-zinc-100">
                  {cols.map((c) => (
                    <td key={c.key} className="px-1 py-1">
                      {c.type === "date" ? (
                        <Input type="date" value={row[c.key] || ""} onChange={(e) => updateRow(idx, c.key, e.target.value)} disabled={readOnly} className="h-8" data-testid={`f-${field.key}-${idx}-${c.key}`} />
                      ) : c.type === "number" ? (
                        <Input type="number" value={row[c.key] ?? ""} onChange={(e) => updateRow(idx, c.key, e.target.value === "" ? "" : Number(e.target.value))} disabled={readOnly} className="h-8" data-testid={`f-${field.key}-${idx}-${c.key}`} />
                      ) : c.type === "choice" ? (
                        <Select value={row[c.key] || "_none"} onValueChange={(v) => updateRow(idx, c.key, v === "_none" ? "" : v)} disabled={readOnly}>
                          <SelectTrigger className="h-8" data-testid={`f-${field.key}-${idx}-${c.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— none —</SelectItem>
                            {(c.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={row[c.key] || ""} onChange={(e) => updateRow(idx, c.key, e.target.value)} disabled={readOnly} className="h-8" data-testid={`f-${field.key}-${idx}-${c.key}`} />
                      )}
                    </td>
                  ))}
                  {!readOnly && (
                    <td className="px-1 py-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-700" onClick={() => removeRow(idx)}
                        data-testid={`f-${field.key}-${idx}-remove`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={cols.length + (readOnly ? 0 : 1)} className="px-2 py-3 text-center text-xs text-zinc-500">No rows.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={addRow} data-testid={`f-${field.key}-add`}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add row
          </Button>
        )}
      </div>
    );
  }
  return <div className="text-sm text-zinc-500">Unsupported field type: {field.type}</div>;
}

export default FieldRenderer;
